import { useMemo } from 'react';

export interface HelpContent {
  route: string;
  title: string;
  module?: string;
  tags?: string[];
  body: string;
  source: 'markdown' | 'mock';
}

// Eagerly import every markdown file under src/content/help as raw text.
// `import.meta.glob` keeps the bundle tree-shaken to whatever exists.
const rawMarkdown = import.meta.glob('/src/content/help/**/*.md', {
  eager: true,
  query: '?raw',
  import: 'default',
}) as Record<string, string>;

interface ParsedFrontmatter {
  route?: string;
  title?: string;
  module?: string;
  tags?: string[];
}

function parseFrontmatter(raw: string): { fm: ParsedFrontmatter; body: string } {
  if (!raw.startsWith('---')) return { fm: {}, body: raw };
  const end = raw.indexOf('\n---', 3);
  if (end === -1) return { fm: {}, body: raw };
  const header = raw.slice(3, end).trim();
  const body = raw.slice(end + 4).replace(/^\n/, '');
  const fm: ParsedFrontmatter = {};
  for (const line of header.split('\n')) {
    const match = line.match(/^([a-zA-Z_]+)\s*:\s*(.*)$/);
    if (!match) continue;
    const [, key, valueRaw] = match;
    const value = valueRaw.trim();
    if (key === 'tags') {
      const arr = value.replace(/^\[|\]$/g, '').split(',').map((s) => s.trim()).filter(Boolean);
      fm.tags = arr;
    } else if (key === 'route' || key === 'title' || key === 'module') {
      fm[key] = value.replace(/^['"]|['"]$/g, '');
    }
  }
  return { fm, body };
}

const MOCK_CONTENT: Record<string, HelpContent> = {
  '/dashboard': {
    route: '/dashboard',
    title: 'Dashboard Ejecutivo',
    module: 'dashboard',
    body: 'Visión global de KPIs. OEE Global, Drift Score, anomalías detectadas. Haz clic en cualquier KPI para ver detalle.',
    source: 'mock',
  },
  '/production/dispatch': {
    route: '/production/dispatch',
    title: 'Dispatch Board',
    module: 'production',
    body: 'Gestiona órdenes de trabajo activas. Arrastra WOs entre columnas para cambiar estado. Usa los filtros para ver por línea o turno.',
    source: 'mock',
  },
  '/quality/nc-board': {
    route: '/quality/nc-board',
    title: 'Tablero de No Conformidades',
    module: 'quality',
    body: 'Lista y gestiona NCs. Puedes disponer, asignar y cerrar NCs desde esta vista.',
    source: 'mock',
  },
};

function buildHelpIndex(): Map<string, HelpContent> {
  const index = new Map<string, HelpContent>();
  for (const [path, raw] of Object.entries(rawMarkdown)) {
    if (path.includes('/_')) continue; // skip _faq, _glossary
    const { fm, body } = parseFrontmatter(raw);
    if (!fm.route) continue;
    index.set(fm.route, {
      route: fm.route,
      title: fm.title ?? fm.route,
      module: fm.module,
      tags: fm.tags,
      body,
      source: 'markdown',
    });
  }
  // Apply mocks only as a fallback for keys not covered by markdown.
  for (const [route, content] of Object.entries(MOCK_CONTENT)) {
    if (!index.has(route)) index.set(route, content);
  }
  return index;
}

const HELP_INDEX = buildHelpIndex();

/**
 * Returns the help entry for the given pathname, or null if none exists.
 * Matches the exact pathname and also falls back to `/` for `/dashboard`
 * so the existing dashboard.md (route: "/") covers both.
 */
export function useHelpContent(pathname: string): HelpContent | null {
  return useMemo(() => {
    if (HELP_INDEX.has(pathname)) return HELP_INDEX.get(pathname) ?? null;
    if (pathname === '/' && HELP_INDEX.has('/dashboard')) return HELP_INDEX.get('/dashboard') ?? null;
    if (pathname === '/dashboard' && HELP_INDEX.has('/')) return HELP_INDEX.get('/') ?? null;
    return null;
  }, [pathname]);
}
